import { useEffect, useState } from "react";
import { useStore } from "zustand";

import { useAppConfig } from "../hooks/useAppConfig";
import type { UseAppConfigResult } from "../hooks/useAppConfig";
import { useCameraSlots } from "../hooks/useCameraSlots";
import { useConnectionGate, isSessionEstablished } from "../hooks/useConnectionGate";
import { useOperatorSession } from "../hooks/useOperatorSession";
import { useRobot } from "../hooks/useRobot";
import { useRobotState } from "../hooks/useRobotState";
import { useTeleopActions } from "../hooks/useTeleopActions";
import { deriveStatusText } from "../lib/session-status-labels";
import { levelLabelRu } from "../modules/connection-stats/ui/labels";
import { resolveCommandCatalog } from "../modules/teleop-state";
import { ConnectionSplash } from "../components/ConnectionSplash";
import { RobotStateProvider } from "../components/RobotStateProvider";
import { StatusChip } from "../components/StatusChip";
import { VideoGrid } from "../components/VideoGrid";
import { useOperatorLogs, DataChannelLogs } from "../modules/teleop-logs";
import type { TeleopSession } from "../modules/teleop-session";
import type { TeleopCommands } from "../modules/teleop-commands";
import styles from "./ConsolePage.module.scss";

/**
 * ConsolePage — split-screen demo: FOUR fully independent operator panels in one tab,
 * both aimed at the SAME room from the URL.
 *
 * "Independent" means each panel runs its own full stack end to end — its own
 * useAppConfig/useOperatorSession/useRobot/useCameraSlots/useOperatorLogs, its own
 * WebRTC peer connection, its own robot-state instance. Nothing is shared between
 * panels except the room id (both read it from the same URL) — this is not a demo of
 * "two widgets sharing one session," it's two separate sessions that happen to join
 * the same room, which is exactly what a real split-screen / multi-operator view
 * needs to prove is possible.
 *
 * Each panel's `useOperatorSession` gets a fresh in-memory peerId (the default —
 * `persistPeerId` is NOT set here), so the two connections can never collide on one
 * cached identity — see teleop-session/README.md and useOperatorSession's
 * `persistPeerId` doc. No manual per-panel coordination needed.
 *
 * Feature set is intentionally cut down from ViewerPage: no joystick/gamepad drive
 * control, no speed-limit sliders — just posture commands (dynamic from the robot's
 * own command catalog when it advertises one, otherwise the fixed stand up/down pair),
 * each panel's own cameras, its own robot-reported channel quality, and its own
 * sent/received command logs. Reduced scope, but every piece of it is a real connection, not a
 * mock — that's the point of the demo.
 */
export function ConsolePage() {
  return (
    <main className={styles.root}>
      <div className={styles.grid}>
        <div className={styles.pane}>
          <ConsolePanel label="Оператор A" />
          <ConsolePanel label="Оператор C" />
        </div>
        <div className={styles.pane}>
          <ConsolePanel label="Оператор B" />
          <ConsolePanel label="Оператор D" />
        </div>
      </div>
    </main>
  );
}

/**
 * One independent operator connection: owns its whole stack from config fetch to
 * robot-state, wrapped in its own RobotStateProvider so nothing here reaches across
 * into the other panel's state.
 */
function ConsolePanel({ label }: { label: string }) {
  const config = useAppConfig();
  const logs = useOperatorLogs();
  const { session, robotState, commands } = useOperatorSession(config, { log: logs.log });

  return (
    <RobotStateProvider robotState={robotState}>
      <ConsolePanelBody
        label={label}
        config={config}
        session={session}
        commands={commands}
        logs={logs}
      />
    </RobotStateProvider>
  );
}

function ConsolePanelBody({
  label,
  config,
  session,
  commands,
  logs,
}: {
  label: string;
  config: UseAppConfigResult;
  session: TeleopSession;
  commands: TeleopCommands;
  logs: ReturnType<typeof useOperatorLogs>;
}) {
  const sessionPhase = useStore(session, (s) => s.phase);
  const status = useStore(session, (s) => s.status);
  const videoStalled = useStore(session, (s) => s.videoStalled);
  const videoApi = useStore(session, (s) => s.videoApi);

  // Transient human-readable messages for actions (reconnect result) — the store's
  // own `status` is always a raw code; a fresh status report supersedes any of these.
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  useEffect(() => setActionMessage(null), [status]);
  const statusText = actionMessage ?? deriveStatusText(status, sessionPhase, videoStalled);

  const handleReconnect = async () => {
    setActionMessage("Переподключение…");
    try {
      await session.reconnect();
      // No lingering "Сессия переподключена" — once this resolves the session is
      // back to ACTIVE, so fall through to statusText's own "Сессия активна".
      setActionMessage(null);
    } catch (err) {
      setActionMessage(`Переподключение не удалось: ${err}`);
    }
  };

  const robot = useRobot();
  const robotState = useRobotState();
  const teleopAllowed = isSessionEstablished(sessionPhase);
  const actions = useTeleopActions({ commands, robotState, teleopAllowed });
  // Robot-declared command catalog (see teleop-state's command-catalog.ts) — a robot
  // that hasn't been upgraded to send `commands` yet (or sends one that fails
  // validation) resolves this to null, and the posture row below falls back to the
  // original fixed stand up/down buttons, unchanged from before this feature.
  const catalogResult = robot.config ? resolveCommandCatalog(robot.config) : null;
  const postureCommands = catalogResult?.ok ? catalogResult.catalog.postures : null;
  const camera = useCameraSlots({
    session: {
      commands,
      videoApi,
      sessionReady: teleopAllowed,
      setStatus: setActionMessage,
    },
    robot,
  });
  const { connectionState, phase } = useConnectionGate(
    config,
    { phase: sessionPhase, videoStalled },
    robot
  );

  if (!config.sessionId) {
    return (
      <div className={styles.panelMessage}>
        Укажите комнату в URL: <code>/console/demo-room</code>
      </div>
    );
  }
  if (config.status === "error") {
    return (
      <div className={`${styles.panelMessage} ${styles.error}`}>
        Ошибка конфигурации: {config.error}
      </div>
    );
  }

  const link = robot.linkQuality;

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <h3 className={styles.panelTitle}>{label}</h3>
        {phase === "ready" ? (
          <StatusChip
            icon="bi-broadcast"
            label="Канал"
            value={link ? `${levelLabelRu(link.level)} · ${link.rtt_ms ?? "—"}мс` : "—"}
            level={link?.level}
          />
        ) : null}
      </div>
      <p className={styles.panelStatus}>
        Комната <b>{config.sessionId}</b> · {statusText}
      </p>

      {phase === "ready" ? (
        <div className={styles.panelReady}>
          <VideoGrid
            cameras={robot.cameras}
            streams={camera.streams}
            metrics={camera.metrics}
            cameraEnabled={camera.cameraEnabled}
            status={statusText}
            onCameraToggle={camera.handleCameraToggle}
            toggleDisabled={!isSessionEstablished(sessionPhase)}
            requestKeyframes={camera.requestKeyframes}
            restoreSlot={camera.restoreSlot}
          />

          <div className={styles.postureRow} role="group" aria-label="Команды позы">
            {postureCommands ? (
              postureCommands.map((command) => (
                <button
                  key={command.command_id}
                  type="button"
                  className={`${styles.posture} ${
                    command.type === "posture.stand_down" ? styles.down : styles.up
                  }`}
                  disabled={actions.postureCooldown || !teleopAllowed}
                  onClick={() => actions.sendPostureCommand(command.type)}
                >
                  <i
                    className={`bi ${
                      command.type === "posture.stand_down"
                        ? "bi-arrow-down-circle-fill"
                        : "bi-arrow-up-circle-fill"
                    } ${styles.postureIcon}`}
                    aria-hidden="true"
                  />
                  <span>{command.label ?? command.command_id}</span>
                </button>
              ))
            ) : (
              <>
                <button
                  type="button"
                  className={`${styles.posture} ${styles.up}`}
                  disabled={actions.postureCooldown || !teleopAllowed}
                  onClick={() => actions.sendPosture("stand_up")}
                >
                  <i
                    className={`bi bi-arrow-up-circle-fill ${styles.postureIcon}`}
                    aria-hidden="true"
                  />
                  <span>Встать</span>
                </button>
                <button
                  type="button"
                  className={`${styles.posture} ${styles.down}`}
                  disabled={actions.postureCooldown || !teleopAllowed}
                  onClick={() => actions.sendPosture("stand_down")}
                >
                  <i
                    className={`bi bi-arrow-down-circle-fill ${styles.postureIcon}`}
                    aria-hidden="true"
                  />
                  <span>Лечь</span>
                </button>
              </>
            )}
          </div>
          <DataChannelLogs incoming={logs.incoming} outgoing={logs.outgoing} />
        </div>
      ) : (
        <ConnectionSplash
          connectionState={connectionState}
          configError={config.error}
          statusText={statusText}
          onReconnect={handleReconnect}
          logLines={logs.system}
          leaving={phase === "leaving"}
        />
      )}
    </div>
  );
}
